import { fmtAge, tierLabel } from '@sikhio/shared';
import type { FallbackTier } from '@sikhio/shared';
import { ageClass, ageIsMeaningful, ageLabel } from '../lib/panelHeader';

interface PanelHeaderProps {
  /** Section title — rendered as a real h2 (not an eyebrow), so screen
   *  readers and document outlines see a proper heading hierarchy.
   *  Optional: omit it when the panel already sits inside a region that
   *  carries its own heading (a rail CollapsibleSection). Repeating the
   *  label there paints the same words twice, a few lines apart, and the
   *  second copy says nothing the first did not. */
  title?: string;
  /** Optional sub-line under the title — short Thai / English
   *  description that gives the panel context in one glance. */
  subtitle?: string;
  /** Data age in minutes from NormalizedFeed.meta.ageMinutes */
  ageMinutes?: number | null;
  /** Fallback tier from NormalizedFeed.meta.fallbackTier */
  fallbackTier?: FallbackTier;
  /** Short source identifier e.g. "open-meteo" | "aqicn" */
  source?: string;
  /** Extra content on the right side (e.g. a refresh button) */
  actions?: React.ReactNode;
}

/**
 * PanelHeader — standard section header for every dashboard panel.
 *
 * Structure:
 *   title  (sans, 600 weight, h2)              |  source · tier · age · actions
 *   subtitle? (sans, lighter, optional)        |    — same row —
 *
 * The title is a real h2, not a styled span. Screen readers and
 * document outlines now see the section hierarchy they were missing.
 *
 * There is deliberately no eyebrow above the title. It used to render the
 * literal word "SECTION" on every panel — nine identical labels down the
 * rails carrying no information, which is exactly the noise this dashboard
 * should not have. The title is the marker.
 */
export function PanelHeader({ title, subtitle, ageMinutes, fallbackTier, source, actions }: PanelHeaderProps) {
  const tier = fallbackTier ?? 'live';
  const tl = tierLabel(tier);

  return (
    <div className="panel-hdr">
      {(title || subtitle) && (
        <div className="panel-hdr-text">
          {title && <h2 className="panel-hdr-title">{title}</h2>}
          {subtitle && <span className="panel-hdr-sub">{subtitle}</span>}
        </div>
      )}

      <div className="panel-hdr-meta">
        {source && (
          <span className="panel-source-tag mono" title={`Data source: ${source}`}>
            {source.toUpperCase()}
          </span>
        )}

        {tl && (
          <span
            className={`panel-tier-warn mono${tier === 'unavailable' ? ' panel-tier-warn--crit' : ''}`}
            title={`Data quality: ${tier} — not live`}
          >
            {tl}
          </span>
        )}

        {(ageMinutes != null || tier === 'unavailable') && (
          <span
            className={`data-age mono ${ageClass(ageMinutes, tier)}`}
            title={
              tier === 'unavailable'
                ? `Feed unavailable · tier: ${tier}`
                : ageIsMeaningful(tier)
                  ? `Observed ${fmtAge(ageMinutes)} ago · tier: ${tier}`
                  : `Fetched ${fmtAge(ageMinutes)} ago, but this is ${tier} data — the reading itself is not that recent`
            }
          >
            {tier === 'unavailable' ? 'OFFLINE' : ageIsMeaningful(tier) ? ageLabel(ageMinutes, tier, fmtAge) : null}
          </span>
        )}

        {actions}
      </div>
    </div>
  );
}
